import { useLocation, useNavigate } from "react-router-dom";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import Gastos from "./Gastos";
import Compras from "./Compras";

/**
 * Gastos y compras, en una sola pantalla.
 *
 * Eran dos entradas del menú en dos grupos distintos —compras en «Producto»,
 * gastos en «Gestión»— y las dos son lo mismo visto desde la caja: dinero que
 * sale. Quien buscaba lo que se le debe al proveedor tenía que acordarse de en
 * cuál de los dos sitios estaba.
 *
 * Lo que **no** se junta son las listas, y es a propósito. Una compra dice lo
 * que se debe; el gasto dice que se pagó, y por eso un gasto puede apuntar a la
 * compra que salda. Mezclarlas en un único listado contaría el mismo dinero dos
 * veces, que es peor que tenerlo en dos sitios. Van en pestañas: una pantalla,
 * cada cosa con sus cuentas.
 *
 * Cada pestaña conserva su dirección (`/gastos`, `/compras`,
 * `/compras/proveedores`), asi que los enlaces de siempre y el botón de atrás
 * del navegador siguen funcionando.
 */

type Pestana = "gastos" | "compras" | "proveedores";

const RUTAS: Record<Pestana, string> = {
  gastos: "/gastos",
  compras: "/compras",
  proveedores: "/compras/proveedores",
};

export default function GastosYCompras() {
  const { t } = useIdioma();
  const { pathname } = useLocation();
  const navegar = useNavigate();
  const { puede } = useSesion();

  const puedeGastos = puede("gestionarGastos");
  const puedeCompras = puede("gestionarInventario");

  const pedida: Pestana = pathname.startsWith("/compras/proveedores")
    ? "proveedores"
    : pathname.startsWith("/compras")
      ? "compras"
      : "gastos";

  /*
   * A una pestaña se puede llegar por un enlace guardado o por el menú, que
   * lleva siempre a la primera. Si la cuenta no la abre, se enseña la otra en
   * vez de una pantalla de errores del servidor.
   */
  const actual: Pestana =
    pedida === "gastos" && !puedeGastos && puedeCompras
      ? "compras"
      : pedida !== "gastos" && !puedeCompras && puedeGastos
        ? "gastos"
        : pedida;

  const boton = (pestana: Pestana, texto: string) => (
    <button
      className={`boton ${actual === pestana ? "" : "secundario"}`}
      onClick={() => navegar(RUTAS[pestana])}
    >
      {texto}
    </button>
  );

  const pestanas = (
    <>
      {puedeGastos && boton("gastos", t("gc.gastos"))}
      {puedeCompras && (
        <>
          {boton("compras", t("gc.compras"))}
          {boton("proveedores", t("gc.proveedores"))}
        </>
      )}
    </>
  );

  if (actual === "gastos") return <Gastos pestanas={pestanas} />;
  return <Compras pestanas={pestanas} vista={actual === "proveedores" ? "proveedores" : "compras"} />;
}
